import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../config/api';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { ArrowLeft, ArrowRight, BookOpen, Clock } from 'lucide-react';

const API = API_URL;

function isProbablyHtml(content) {
  if (!content) return false;
  const htmlPattern = /<[^>]+>/g;
  return htmlPattern.test(content);
}

function sanitizeHtml(html) {
  if (!html) return '';
  let safe = html;
  
  // Remove script tags completely
  safe = safe.replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, '');
  
  // Remove inline event handlers like onclick="" or onload=''
  safe = safe.replace(/\son\w+="[^"]*"/gi, '');
  safe = safe.replace(/\son\w+='[^']*'/gi, '');
  
  // Remove javascript: URLs
  safe = safe.replace(/href\s*=\s*["']javascript:[^"']*["']/gi, 'href="#"');
  
  // Sanitize style attributes - allow only safe CSS properties
  // This keeps color, background-color, text-align, font-weight, font-style, text-decoration
  safe = safe.replace(/style\s*=\s*["']([^"']*)["']/gi, (match, styleContent) => {
    // Parse and filter style properties
    const allowedProperties = [
      'color', 'background-color', 'background',
      'text-align', 'text-decoration', 'text-indent',
      'font-weight', 'font-style', 'font-size', 'font-family',
      'line-height', 'letter-spacing', 'word-spacing',
      'margin', 'margin-top', 'margin-bottom', 'margin-left', 'margin-right',
      'padding', 'padding-top', 'padding-bottom', 'padding-left', 'padding-right',
      'border', 'border-left', 'border-right', 'border-top', 'border-bottom',
      'display', 'vertical-align', 'white-space'
    ];
    
    const filteredStyles = styleContent
      .split(';')
      .map(s => s.trim())
      .filter(s => {
        if (!s) return false;
        const prop = s.split(':')[0]?.trim().toLowerCase();
        // Check if property is allowed and doesn't contain expressions/urls for injection
        const isAllowed = allowedProperties.some(allowed => prop === allowed || prop.startsWith(allowed + '-'));
        const hasNoInjection = !/expression|javascript|url\s*\(/i.test(s);
        return isAllowed && hasNoInjection;
      })
      .join('; ');
    
    return filteredStyles ? `style="${filteredStyles}"` : '';
  });
  
  return safe;
}

const NovelReader = () => {
  const { novelId, chapterId } = useParams();
  const navigate = useNavigate();

  const [novel, setNovel] = useState(null);
  const [chapter, setChapter] = useState(null);
  const [chapters, setChapters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError('');
      try {
        const chapterUrl = `${API}/novel-chapters/${chapterId}`;

        try {
          const res = await axios.get(chapterUrl);
          setChapter(res.data);
        } catch (err) {
          console.error('NovelReader fetch failed:', {
            url: chapterUrl,
            status: err?.response?.status,
            body: err?.response?.data,
            novelId,
            chapterId,
          });
          setError('Failed to load chapter. It may be unavailable or unpublished.');
          setLoading(false);
          return;
        }

        try {
          const [novelRes, chaptersRes] = await Promise.all([
            axios.get(`${API}/novels/${novelId}`),
            axios.get(`${API}/novels/${novelId}/chapters`),
          ]);

          setNovel(novelRes.data);
          setChapters(Array.isArray(chaptersRes.data) ? chaptersRes.data : chaptersRes.data || []);
        } catch (err) {
          console.error('Error loading novel or chapters list:', {
            novelId,
            chapterId,
            error: err?.message,
            responseStatus: err?.response?.status,
            responseData: err?.response?.data,
          });
          // keep chapter if we already have it; only show a softer error
        }
      } finally {
        setLoading(false);
      }
    };

    if (novelId && chapterId) {
      fetchData();
    }
  }, [novelId, chapterId]);

  const { prevChapter, nextChapter } = useMemo(() => {
    if (!chapters || !chapter) return { prevChapter: null, nextChapter: null };
    const idx = chapters.findIndex((c) => (c.chapter_id || c.id) === (chapter.chapter_id || chapter.id));
    if (idx === -1) return { prevChapter: null, nextChapter: null };
    return {
      prevChapter: idx > 0 ? chapters[idx - 1] : null,
      nextChapter: idx < chapters.length - 1 ? chapters[idx + 1] : null,
    };
  }, [chapters, chapter]);

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-orange-500 mx-auto mb-4" />
          <div className="text-xl font-medium">Loading chapter...</div>
        </div>
      </div>
    );
  }

  if (error || !chapter || !novel) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-center px-4">
          <h2 className="text-2xl font-bold mb-3">Chapter not available</h2>
          <p className="text-gray-400 mb-6">{error || 'This chapter could not be found or is not accessible.'}</p>
          <Link to={`/novel/${novelId}`}>
            <Button className="bg-orange-500 hover:bg-orange-600">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Novel
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const content = chapter.content || '';
  const contentType = chapter.content_type || '';

  const isHtmlContent = contentType.toLowerCase() === 'html' || isProbablyHtml(content);
  const safeHtml = isHtmlContent ? sanitizeHtml(content) : '';

  const renderPlainText = (text) => {
    if (!text) return null;
    const paragraphs = text.split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean);
    if (paragraphs.length === 0) {
      return <p className="text-gray-300">No content available.</p>;
    }
    return (
      <div className="space-y-5">
        {paragraphs.map((para, idx) => {
          const lines = para.split('\n');
          return (
            <p key={idx} className="text-gray-100 text-base md:text-lg leading-7 md:leading-8">
              {lines.map((line, i) => (
                <React.Fragment key={i}>
                  {line}
                  {i !== lines.length - 1 && <br />}
                </React.Fragment>
              ))}
            </p>
          );
        })}
      </div>
    );
  };

  const publishedDate = chapter.published_at || chapter.created_at;

  return (
    <div className="min-h-screen bg-black text-white">
      <div className="bg-gradient-to-r from-orange-500/10 to-red-500/10 border-b border-orange-500/30">
        <div className="container mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Button
              variant="ghost"
              className="text-gray-300 hover:text-white"
              onClick={() => navigate(-1)}
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              Back
            </Button>
            <div>
              <p className="text-xs text-gray-400 uppercase tracking-wide">Reading</p>
              <h1 className="text-lg font-bold">
                {novel.title}
              </h1>
            </div>
          </div>
          <BookOpen className="w-6 h-6 text-orange-400" />
        </div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 py-8 flex justify-center">
        <Card className="w-full max-w-3xl bg-gray-900 border border-gray-800 shadow-xl">
          <CardContent className="p-6 sm:p-8">
            <header className="mb-6">
              <h2 className="text-3xl font-bold mb-2">
                {chapter.title || `Chapter ${chapter.chapter_number}`}
              </h2>
              <div className="flex items-center justify-between text-sm text-gray-400">
                <span>
                  Chapter {chapter.chapter_number}
                </span>
                {publishedDate && (
                  <span className="flex items-center">
                    <Clock className="w-3 h-3 mr-1" />
                    {new Date(publishedDate).toLocaleDateString()}
                  </span>
                )}
              </div>
            </header>

            <main className="mt-6">
              {isHtmlContent ? (
                <div
                  className="prose prose-invert max-w-none text-base md:text-lg leading-7 md:leading-8 space-y-5"
                  dangerouslySetInnerHTML={{ __html: safeHtml }}
                />
              ) : (
                renderPlainText(content)
              )}
            </main>

            <footer className="mt-10 pt-6 border-t border-gray-800 flex flex-col sm:flex-row items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={!prevChapter}
                  onClick={() => prevChapter && navigate(`/novel/${novelId}/read/${prevChapter.id}`)}
                  className="border-gray-700 text-gray-300 hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ArrowLeft className="w-4 h-4 mr-1" />
                  Prev Chapter
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={!nextChapter}
                  onClick={() => nextChapter && navigate(`/novel/${novelId}/read/${nextChapter.id}`)}
                  className="border-gray-700 text-gray-300 hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next Chapter
                  <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate(`/novel/${novelId}`)}
                className="border-orange-500 text-orange-400 hover:bg-orange-500 hover:text-white"
              >
                Back to Novel
              </Button>
            </footer>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default NovelReader;
